import { SectionSummary } from '@/components/WorkspacePresentation';
/** Crawled page explorer: every URL discovered and the signals captured for it. */

import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { crawlApi, siteApi } from '@/api/endpoints';
import {
  Badge,
  Card,
  EmptyState,
  ErrorState,
  FilterBar,
  FilterField,
  LoadingState,
  PageHeader,
  Table,
  Td,
  Th,
  UrlText,
} from '@/components/ui';
import { DocumentIcon } from '@/components/icons';
import { filterInputClass, formatNumber } from '@/utils/format';
import { Pagination } from '@/features/issues/IssuesPage';

export function PagesPage() {
  const { siteId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const page = Number(params.get('page') ?? '1');
  const search = params.get('search') ?? '';
  const orphansOnly = params.get('orphans') === '1';
  const sort = params.get('sort') ?? 'depth';

  const dashboardQuery = useQuery({
    queryKey: ['dashboard', siteId],
    queryFn: () => siteApi.dashboard(siteId),
  });
  const crawlId = dashboardQuery.data?.last_crawl?.id;

  const pagesQuery = useQuery({
    queryKey: ['crawl-pages', crawlId, page, search, orphansOnly, sort],
    queryFn: () =>
      crawlApi.pages(crawlId!, {
        page,
        page_size: 50,
        search: search || undefined,
        orphans_only: orphansOnly || undefined,
        sort,
      }),
    enabled: Boolean(crawlId),
  });

  function setParam(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== 'page') next.delete('page');
    setParams(next, { replace: true });
  }

  if (dashboardQuery.isLoading) return <LoadingState />;
  if (!crawlId) {
    return (
      <>
        <PageHeader title="Pages" />
        <EmptyState
          icon={<DocumentIcon className="h-5 w-5" />}
          title="No crawl yet"
          description="Run a crawl from the overview to discover this website's pages."
        />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Pages"
        description="Every URL found in the most recent crawl, with the SEO signals extracted from it."
      />

      {pagesQuery.data && <SectionSummary items={[
        { label: 'Matching pages', value: pagesQuery.data.total, detail: 'In the latest crawl' },
        { label: 'Indexable in view', value: pagesQuery.data.items.filter(page => page.is_indexable).length, detail: 'No observed indexing block' },
        { label: 'Orphans in view', value: pagesQuery.data.items.filter(page => page.is_orphan).length, detail: 'No observed internal link' },
      ]} note="Indexability and orphan counts apply to the loaded page, and do not confirm Google indexing." />}

      <FilterBar>
        <FilterField label="Search" htmlFor="page-search" className="min-w-[14rem] flex-1">
          <input
            id="page-search"
            defaultValue={search}
            onBlur={(event) => setParam('search', event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') setParam('search', event.currentTarget.value);
            }}
            placeholder="URL or title"
            className={filterInputClass}
          />
        </FilterField>
        <FilterField label="Sort by" htmlFor="page-sort">
          <select
            id="page-sort"
            value={sort}
            onChange={(event) => setParam('sort', event.target.value)}
            className={filterInputClass}
          >
            <option value="depth">Crawl depth</option>
            <option value="links_in">Internal links in</option>
            <option value="pagerank">Internal PageRank</option>
            <option value="words">Word count</option>
            <option value="status">Status code</option>
            <option value="url">URL</option>
          </select>
        </FilterField>
        <label className="flex items-center gap-2 pb-2 text-sm text-ink-muted">
          <input
            type="checkbox"
            checked={orphansOnly}
            onChange={(event) => setParam('orphans', event.target.checked ? '1' : '')}
            className="h-4 w-4 rounded border-border-strong text-brand focus:ring-brand"
          />
          Orphans only
        </label>
      </FilterBar>

      {pagesQuery.isLoading ? <LoadingState label="Loading pages" /> : null}
      {pagesQuery.isError ? (
        <ErrorState
          message={(pagesQuery.error as Error).message}
          onRetry={() => void pagesQuery.refetch()}
        />
      ) : null}

      {pagesQuery.data && pagesQuery.data.items.length === 0 ? (
        <EmptyState
          icon={<DocumentIcon className="h-5 w-5" />}
          title="No pages match these filters"
          description="Clear the search or the orphans-only filter to see the rest of the crawl."
        />
      ) : null}

      {pagesQuery.data && pagesQuery.data.items.length > 0 ? (
        <Card>
          <Table>
            <thead>
              <tr>
                <Th>URL</Th>
                <Th align="center">Status</Th>
                <Th align="center">Indexable</Th>
                <Th align="right">Depth</Th>
                <Th align="right">Links in</Th>
                <Th align="right">Words</Th>
              </tr>
            </thead>
            <tbody>
              {pagesQuery.data.items.map((crawlPage) => (
                <tr key={crawlPage.id} className="hover:bg-surface-muted">
                  <Td>
                    <UrlText url={crawlPage.normalized_url} />
                    {crawlPage.title ? (
                      <span className="mt-0.5 block truncate text-sm text-ink">
                        {crawlPage.title}
                      </span>
                    ) : (
                      <span className="mt-0.5 block text-xs text-danger">no title</span>
                    )}
                    {crawlPage.is_orphan ? (
                      <Badge tone="warning" className="mt-1">
                        orphan
                      </Badge>
                    ) : null}
                  </Td>
                  <Td align="center">
                    <Badge
                      tone={
                        !crawlPage.status_code
                          ? 'neutral'
                          : crawlPage.status_code >= 500
                            ? 'critical'
                            : crawlPage.status_code >= 400
                              ? 'danger'
                              : crawlPage.status_code >= 300
                                ? 'warning'
                                : 'success'
                      }
                    >
                      {crawlPage.status_code ?? crawlPage.fetch_outcome}
                    </Badge>
                  </Td>
                  <Td align="center">
                    {crawlPage.is_indexable ? (
                      <Badge tone="success">yes</Badge>
                    ) : (
                      <Badge tone="neutral" className="cursor-help">
                        <span title={crawlPage.indexability_reason ?? undefined}>no</span>
                      </Badge>
                    )}
                  </Td>
                  <Td align="right" className="tnum text-sm">
                    {crawlPage.depth}
                  </Td>
                  <Td align="right" className="tnum text-sm">
                    {crawlPage.unique_internal_referrers}
                  </Td>
                  <Td align="right" className="tnum text-sm">
                    {formatNumber(crawlPage.word_count)}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>

          {pagesQuery.data.pages > 1 ? (
            <Pagination
              page={pagesQuery.data.page}
              pages={pagesQuery.data.pages}
              total={pagesQuery.data.total}
              onChange={(next) => setParam('page', String(next))}
            />
          ) : null}
        </Card>
      ) : null}
    </>
  );
}
